<template>
    <Head title="Coaching setup" />
    <GuidedSetupPage
        v-if="ready"
        :setup="setup"
        :chat-input="chatInput"
        :invite-url="collaboration_profile_invite_url"
        :directory-enabled="collaboration_profile_directory_enabled" />
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
/**
 * Calendar OAuth returns with ?setup= to reopen the relevant step, and ?calendar_connected= to mark the one
 * ask that finished while the user was off the page. Remove both after handling them so refreshes neither
 * replay the navigation nor recount the connection. Mirrors `SETUP_STEP_PARAM` and `SETUP_CONNECTED_PARAM`
 * in `calendar_first/setup.py`.
 */
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { DismissNotificationRequest } from "~vue/api/generated/navigation";
import type { GuidedSetupOrderTestEnrollment } from "~vue/api/generated/onboarding";
import { dismissNotificationLocallyKey, type GlobalProperties } from "~vue/appTypes";
import { MainMenuPane } from "~vue/components/navigation/menuPanes";
import GuidedSetupPage from "~vue/ProfileSetup/GuidedSetupPage.vue";
import type { ChatInputProps, SetupState, SetupStepId } from "~vue/ProfileSetup/types";
import { useProfileSetup } from "~vue/ProfileSetup/useProfileSetup";
import type { PatternEntry } from "~vue/utils/pii/keywordClassifier";
import { computed, inject, onMounted, onUnmounted, ref } from "vue";

const props = defineProps<{
    profile_setup: SetupState;
    collaboration_profile_invite_url: string;
    collaboration_profile_directory_enabled: boolean;
    event_context: Record<string, string>;
    default_chat_redirect_url: string;
    document_upload_enabled: boolean;
    visual_document_analysis_enabled: boolean;
    work_document_capabilities_url: string;
    pii_patterns: PatternEntry[] | null;
    better_voice_composer_enabled: boolean;
    has_workiq_connected: boolean;
    guided_setup_order_enrollment_pending: boolean;
}>();
const { profile_setup, event_context } = props;

const chatInput = computed<ChatInputProps>(() => ({
    default_chat_redirect_url: props.default_chat_redirect_url,
    document_upload_enabled: props.document_upload_enabled,
    visual_document_analysis_enabled: props.visual_document_analysis_enabled,
    work_document_capabilities_url: props.work_document_capabilities_url,
    pii_patterns: props.pii_patterns,
    better_voice_composer_enabled: props.better_voice_composer_enabled,
    has_workiq_connected: props.has_workiq_connected,
}));

const { $setEventContext } = inject<GlobalProperties>("globalProperties")!;

const setup = useProfileSetup(profile_setup);
const dismissNotificationLocally = inject(dismissNotificationLocallyKey, () => {});

/* A first view joins the step-order test before the recorded visit would rule the user out. Joining assigns the arm
 * that decides the ask order, so the page reloads into it rather than showing an order it is about to change. */
const ready = ref(!props.guided_setup_order_enrollment_pending);

const {
    execute: joinOrderTest,
    data: enrollment,
    error: enrollmentError,
    abort: abortEnrollment,
    aborted: enrollmentAborted,
} = useFetch("/v1/onboarding/guided-setup/order-test/enrollment", { immediate: false }).put().json<GuidedSetupOrderTestEnrollment>();

/* Leaving mid-request must not reload whichever page the user moved on to. */
onUnmounted(() => abortEnrollment());

const visitBody: DismissNotificationRequest = { pane: MainMenuPane.GUIDED_SETUP };
const { execute: recordVisit, onFetchResponse: onVisitRecorded } = useFetch("/v1/navigation/notifications/dismiss", { immediate: false }).post(visitBody).json();
onVisitRecorded(() => dismissNotificationLocally(MainMenuPane.GUIDED_SETUP));

interface SetupEntry {
    requested: SetupStepId | null;
    calendarConnected: boolean;
}

function consumeEntryParams(): SetupEntry {
    const url = new URL(globalThis.location.href);
    const requested = url.searchParams.get("setup");
    const calendarConnected = url.searchParams.get("calendar_connected") === "1";
    if (requested === null && !calendarConnected) {
        return { requested: null, calendarConnected: false };
    }
    url.searchParams.delete("setup");
    url.searchParams.delete("calendar_connected");
    globalThis.history.replaceState(globalThis.history.state, "", url.toString());
    // Fall through to the next outstanding ask for an unknown step, and for one this user was never offered.
    const known = requested !== null && profile_setup.steps.some((step) => step.id === requested);
    return { requested: known ? (requested as SetupStepId) : null, calendarConnected };
}

onMounted(async () => {
    $setEventContext(event_context);
    if (!ready.value) {
        await joinOrderTest();
        if (enrollmentAborted.value) {
            return;
        }
        if (!enrollmentError.value && enrollment.value?.enrolled) {
            /* `router.reload` keeps the page mounted, so the setup would keep the order it was built with. Async, like a
             * reload, so a navigation the user has already started is never interrupted. */
            router.visit(globalThis.location.href, {
                only: ["profile_setup", "guided_setup_order_enrollment_pending"],
                replace: true,
                preserveScroll: true,
                async: true,
            });
            return;
        }
        ready.value = true;
    }
    recordVisit();
    const { requested, calendarConnected } = consumeEntryParams();
    /* Before the ask reopens, because the connection happened before this page load. */
    if (calendarConnected) {
        setup.reportCalendarConnected();
    }
    const step = requested ?? setup.nextUnfinished();
    if (step !== null) {
        /* A calendar connected off-page goes straight to its analysis, past calendar-last's "Connected" screen. */
        const analyze = calendarConnected && step === "calendar" && profile_setup.run.calendar.connected;
        setup.openStep(step, analyze ? "scanning" : null);
    }
});
</script>
